import React from "react";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CloseIcon from "@mui/icons-material/Close";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Avatar, Chip, Dialog, IconButton, Stack, Typography } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { IContact } from "store/types/user-management";

import { contactInitial, contactName, formatAddDate } from "../helpers";

// Brand gold used for the section-header icons in the design.
const SECTION_ICON_COLOR = "#C09560";

interface Props {
  open: boolean;
  contact: IContact | null;
  onClose: () => void;
}

const cardSx = {
  bgcolor: "white",
  borderRadius: "8px",
  boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
  p: "16px",
  gap: "16px"
} as const;

const labelSx = { fontSize: "14px", lineHeight: "16px", color: "#496682" } as const;
const valueSx = { fontSize: "15px", lineHeight: "20px", fontWeight: 500, color: "#071f35" } as const;

// A label/value pair in the Details grid; renders an em dash when the value is missing.
const DetailItem: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <Stack sx={{ flex: "1 0 0", minWidth: 0, gap: "2px", overflow: "hidden" }}>
      <Typography sx={labelSx} noWrap>
        {label}
      </Typography>
      <Typography sx={{ ...valueSx, color: isEmpty ? "#898989" : valueSx.color }} noWrap>
        {isEmpty ? "—" : value}
      </Typography>
    </Stack>
  );
};

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
    {icon}
    <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#071f35" }}>{title}</Typography>
  </Stack>
);

const roleChipSx = {
  height: "24px",
  borderRadius: "20px",
  backgroundColor: "#f6f8fc",
  color: "#071f35",
  fontSize: "12px"
} as const;

// Read-only contact details, opened from the Person link in the Contacts table. All fields come
// from the list row's ContactResource (no fetch-by-id endpoint exists / is needed).
export const ContactModal: React.FC<Props> = ({ open, contact, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "800px",
            maxWidth: "800px",
            m: "16px",
            p: "32px",
            gap: "20px",
            borderRadius: "8px",
            boxShadow: "0px 10px 15px -3px rgba(18,18,23,0.08), 0px 4px 6px -2px rgba(18,18,23,0.05)"
          }
        }
      }}
    >
      {contact && (
        <Stack sx={{ gap: "20px" }}>
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: "6px", pb: "8px", borderBottom: "1px solid #dbe8f5" }}
          >
            <Typography sx={{ flex: "1 0 0", fontSize: "18px", color: "#071f35" }}>Contact</Typography>
            <IconButton onClick={onClose} sx={{ p: "4px" }} aria-label="close">
              <CloseIcon sx={{ fontSize: "16px", color: "#071f35" }} />
            </IconButton>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", gap: "8px", py: "12px" }}>
            <Avatar
              sx={{ width: 40, height: 40, bgcolor: "#ede5f9", color: "#6e45eb", fontSize: "18px", fontWeight: 500 }}
            >
              {contactInitial(contact)}
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35" }} noWrap>
                {contactName(contact) || "—"}
              </Typography>
              {contact.email && (
                <Typography sx={{ fontSize: "14px", lineHeight: "16px", color: "#496682" }} noWrap>
                  {contact.email}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack sx={cardSx}>
            <SectionHeader
              icon={<GroupOutlinedIcon sx={{ fontSize: "20px", color: SECTION_ICON_COLOR }} />}
              title="Details"
            />
            <Stack sx={{ gap: "12px" }}>
              <Stack direction="row" sx={{ gap: "16px" }}>
                <DetailItem label="Title" value={contact.title} />
                <DetailItem label="Skill" value={contact.additional_information?.skill} />
                <DetailItem label="Asset Class" value={contact.additional_information?.asset_class} />
              </Stack>
              <Stack direction="row" sx={{ gap: "16px" }}>
                <DetailItem label="Organization" value={contact.firm} />
                <DetailItem label="Add Date" value={formatAddDate(contact.created_at)} />
                <DetailItem label="Deals" value={contact.deals.length} />
              </Stack>
            </Stack>
          </Stack>

          <Stack sx={cardSx}>
            <SectionHeader
              icon={<BusinessOutlinedIcon sx={{ fontSize: "20px", color: SECTION_ICON_COLOR }} />}
              title="Related to Deals"
            />
            <Stack sx={{ border: "1px solid #dbe8f5", borderRadius: "8px", overflow: "hidden" }}>
              <MuiTable>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f6f8fc" }}>
                    <TableCell sx={{ fontSize: "16px", fontWeight: 500, color: "#071f35", padding: "8px 20px" }}>
                      Deal Name
                    </TableCell>
                    <TableCell sx={{ fontSize: "16px", fontWeight: 500, color: "#071f35", padding: "8px 20px" }}>
                      Role
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contact.deals.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ fontSize: "14px", color: "#898989", padding: "12px 20px", textAlign: "center" }}
                      >
                        No related deals.
                      </TableCell>
                    </TableRow>
                  ) : (
                    contact.deals.map((deal) => (
                      <TableRow key={deal.id}>
                        <TableCell sx={{ fontSize: "16px", color: "#071f35", padding: "12px 20px" }}>
                          {deal.name}
                        </TableCell>
                        <TableCell sx={{ padding: "12px" }}>
                          {deal.deal_role_label ? (
                            <Chip label={deal.deal_role_label} sx={roleChipSx} />
                          ) : (
                            <Typography component="span" sx={{ color: "#898989" }}>
                              {"—"}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </MuiTable>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Dialog>
  );
};
